/**
 * Vision Service
 *
 * Sends images (or video frames) to the backend /api/vision/analyze endpoint,
 * which uses gpt-5.2-chat to produce a text description.
 *
 * The description is then injected as text into the realtime model session so
 * that azure-gpt-realtime can "see" the content without native image support.
 */

const VISION_ENDPOINT = '/api/vision/analyze';

export interface VisionAnalyzeOptions {
  backendUrl: string;
  /** Array of pure base64 strings — NO "data:image/..." prefix */
  images: string[];
  /** MIME type per image, e.g. "image/jpeg". Defaults to "image/jpeg" if omitted. */
  mimeTypes?: string[];
  /** Context-specific prompt for the vision model */
  prompt: string;
  /** User's detected language: 'ar' | 'en' | 'ur' */
  language: 'ar' | 'en' | 'ur';
}

/**
 * Analyze one or more images (or video frames) using the backend vision service (gpt-5.2-chat).
 * Returns a text description in the user's language.
 */
export async function analyzeWithVision(options: VisionAnalyzeOptions): Promise<string> {
  const { backendUrl, images, mimeTypes = [], prompt, language } = options;

  if (images.length === 0) {
    throw new Error('[VisionService] No images provided');
  }

  const url = `${backendUrl.replace(/\/$/, '')}${VISION_ENDPOINT}`;

  const payload = {
    images,
    mime_types: mimeTypes.length > 0 ? mimeTypes : images.map(() => 'image/jpeg'),
    prompt,
    language,
  };

  console.log(`[VisionService] Analyzing ${images.length} image(s) in language: ${language}`);

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`[VisionService] Backend error ${response.status}: ${errorText}`);
  }

  const data = await response.json();
  const description: string = data.response || '';

  console.log(`[VisionService] Analysis complete (${description.length} chars)`);
  return description;
}

/**
 * Convert a File object to a pure base64 string (no data URI prefix).
 */
export async function fileToBase64(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result === 'string') {
        // Remove "data:image/jpeg;base64," prefix
        const base64 = result.split(',')[1];
        resolve(base64);
      } else {
        reject(new Error('Failed to read file'));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Extract N evenly-spaced frames from a video File.
 * Uses an off-screen <video> + <canvas> — same technique as ControlTray.tsx.
 *
 * @param videoFile - The video File object
 * @param frameCount - Number of frames to extract (default: 5)
 * @returns Array of { data: base64, mimeType: 'image/jpeg' }
 */
export async function extractVideoFrames(
  videoFile: File,
  frameCount: number = 5
): Promise<{ data: string; mimeType: string }[]> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('[VisionService] Canvas 2D context unavailable'));
      return;
    }

    const objectUrl = URL.createObjectURL(videoFile);
    video.src = objectUrl;
    video.muted = true;
    video.playsInline = true;

    video.onloadedmetadata = async () => {
      const duration = video.duration;
      if (!duration || duration === Infinity) {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('[VisionService] Video duration unknown'));
        return;
      }

      canvas.width = Math.round(video.videoWidth * 0.5);
      canvas.height = Math.round(video.videoHeight * 0.5);

      const frames: { data: string; mimeType: string }[] = [];

      for (let i = 0; i < frameCount; i++) {
        // Seek to evenly-spaced timestamps (avoid the very first/last 5%)
        const seekTime = (duration * 0.05) + (duration * 0.9 * (i / Math.max(frameCount - 1, 1)));

        await new Promise<void>((seekResolve) => {
          video.onseeked = () => seekResolve();
          video.currentTime = seekTime;
        });

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        const base64 = dataUrl.split(',')[1];
        frames.push({ data: base64, mimeType: 'image/jpeg' });
      }

      URL.revokeObjectURL(objectUrl);
      console.log(`[VisionService] Extracted ${frames.length} frames from video`);
      resolve(frames);
    };

    video.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('[VisionService] Failed to load video for frame extraction'));
    };

    video.load();
  });
}

// ============================================================================
// Typed vision helpers — prompts live in backend JSON templates.
// These functions call dedicated endpoints; no prompt parameter needed.
// ============================================================================

export interface VisionWithContextResult {
  /** Raw description from the vision model */
  description: string;
  /** Pre-formatted message ready to inject into the realtime session */
  context_message: string;
  /** Vision model used (e.g. "gpt-5.2-chat") */
  model: string;
}

export interface AnalyzeImagesOptions {
  backendUrl: string;
  images: string[];
  mimeTypes?: string[];
  language: 'ar' | 'en' | 'ur';
}

async function _callTypedVisionEndpoint(
  endpoint: string,
  options: AnalyzeImagesOptions,
): Promise<VisionWithContextResult> {
  const { backendUrl, images, mimeTypes = [], language } = options;
  const url = `${backendUrl.replace(/\/$/, '')}${endpoint}`;
  const payload = {
    images,
    mime_types: mimeTypes.length > 0 ? mimeTypes : images.map(() => 'image/jpeg'),
    language,
  };
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`[VisionService] ${endpoint} error ${response.status}: ${errorText}`);
  }
  return response.json() as Promise<VisionWithContextResult>;
}

/**
 * Analyze accident scene image(s).
 * Prompt is loaded from backend/prompts/vision/accident_analysis.json.
 */
export async function analyzeAccidentScene(options: AnalyzeImagesOptions): Promise<VisionWithContextResult> {
  console.log(`[VisionService] analyzeAccidentScene — ${options.images.length} image(s), lang=${options.language}`);
  return _callTypedVisionEndpoint('/api/vision/accident-analysis', options);
}

/**
 * Extract vehicle license plate data from an image.
 * Prompt is loaded from backend/prompts/vision/license_extraction.json.
 */
export async function extractLicensePlate(options: AnalyzeImagesOptions): Promise<VisionWithContextResult> {
  console.log(`[VisionService] extractLicensePlate — lang=${options.language}`);
  return _callTypedVisionEndpoint('/api/vision/license-extract', options);
}

/**
 * Describe a single live webcam frame.
 * Prompt is loaded from backend/prompts/vision/live_camera.json.
 */
export async function analyzeLiveCameraFrame(options: AnalyzeImagesOptions): Promise<VisionWithContextResult> {
  return _callTypedVisionEndpoint('/api/vision/live-camera', options);
}
